import type { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from 'geojson';
import basinsGeoJsonUrl from '../../assets/basins/production_areas.geojson?url';

export type BasinProperties = {
  OBJECTID: number;
  Name: string;
  Production: string;
  RuleID: number;
  GlobalID: string;
  SHAPE_Length: number;
  SHAPE_Area: number;
};

export type BasinFeature = Feature<Polygon | MultiPolygon, BasinProperties>;

let basinFeaturesCache: BasinFeature[] | null = null;
let basinFeaturesPromise: Promise<BasinFeature[]> | null = null;

/**
 * Load basin boundaries from the attached GeoJSON for map overlays or basin detection.
 */
export async function loadBasinFeatures(options?: { signal?: AbortSignal }): Promise<BasinFeature[]> {
  const response = await fetch(basinsGeoJsonUrl, { signal: options?.signal });
  if (!response.ok) {
    throw new Error(`Failed to load basin GeoJSON: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as FeatureCollection<Polygon | MultiPolygon, BasinProperties>;
  return data.features || [];
}

// Prime the cache early so synchronous lookups can work later
export function primeBasinFeatures(): void {
  if (!basinFeaturesPromise) {
    basinFeaturesPromise = loadBasinFeatures();
    basinFeaturesPromise.then((features) => {
      basinFeaturesCache = features;
    }).catch(() => {
      // Keep cache null on failure; lookups will gracefully fallback
      basinFeaturesCache = null;
    });
  }
}

export async function ensureBasinFeatures(): Promise<BasinFeature[]> {
  if (basinFeaturesCache) return basinFeaturesCache;
  if (!basinFeaturesPromise) {
    basinFeaturesPromise = loadBasinFeatures();
  }
  basinFeaturesCache = await basinFeaturesPromise;
  return basinFeaturesCache;
}

function pointInRing(point: [number, number], ring: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect = ((yi > point[1]) !== (yj > point[1])) &&
      (point[0] < (xj - xi) * (point[1] - yi) / (yj - yi + 0.0000001) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInPolygon(point: [number, number], polygon: Polygon): boolean {
  const [outer, ...holes] = polygon.coordinates;
  if (!outer) return false;
  if (!pointInRing(point, outer)) return false;
  return !holes.some((hole) => pointInRing(point, hole));
}

function pointInMultiPolygon(point: [number, number], multi: MultiPolygon): boolean {
  return multi.coordinates.some((polyCoords) => pointInPolygon(point, { type: 'Polygon', coordinates: polyCoords }));
}

/**
 * Synchronous basin lookup using cached GeoJSON polygons. Returns basin name or null.
 */
export function getCachedBasinForPoint(lat: number | undefined, lng: number | undefined): string | null {
  if (!basinFeaturesCache || lat === undefined || lng === undefined) return null;
  const point: [number, number] = [lng, lat];
  for (const feature of basinFeaturesCache) {
    if (!feature.geometry) continue;
    if (feature.geometry.type === 'Polygon' && pointInPolygon(point, feature.geometry)) {
      return feature.properties?.Name || null;
    }
    if (feature.geometry.type === 'MultiPolygon' && pointInMultiPolygon(point, feature.geometry)) {
      return feature.properties?.Name || null;
    }
  }
  return null;
}

/**
 * Render basin boundaries onto a Google Map data layer; returns a cleanup function.
 */
export async function addBasinLayer(map: google.maps.Map): Promise<() => void> {
  const features = await ensureBasinFeatures();
  const added: google.maps.Data.Feature[] = [];

  map.data.setStyle({
    fillColor: '#22d3ee',
    fillOpacity: 0.08,
    strokeColor: '#22d3ee',
    strokeWeight: 1.2,
  });

  for (const feature of features) {
    const addedFeatures = map.data.addGeoJson(feature as any);
    added.push(...addedFeatures);
  }

  return () => {
    added.forEach((f) => map.data.remove(f));
  };
}

// Start warming the cache as soon as the module is imported
primeBasinFeatures();
